type DisplayedLevelProgressInput = {
  displayedHighest: number;
  fetchedHighest: number;
  pendingCompletedLevelIndex: number | null;
};

/**
 * The backend saves progress while the completion popup is still open. The path
 * must keep showing the old progress until the popup closes, otherwise the next
 * level is already active before the completion choreography gets to unlock it.
 */
export function syncDisplayedLevelProgress({
  displayedHighest,
  fetchedHighest,
  pendingCompletedLevelIndex,
}: DisplayedLevelProgressInput): number {
  const completionNotYetShown =
    pendingCompletedLevelIndex !== null &&
    pendingCompletedLevelIndex > displayedHighest;
  return completionNotYetShown ? displayedHighest : fetchedHighest;
}

type DisplayedCourseCompletionInput = {
  fetchedCompleted: boolean;
  fetchedHighest: number;
  displayedCompleted: boolean;
  displayedHighest: number;
  pendingCompletedLevelIndex: number | null;
  totalLevels: number;
};

/**
 * Same timing as progress: the silent reload can mark the course complete while
 * the popup is still open. Keep the snake and certificate goal unchanged until
 * the completion sequence starts after the modal closes. Once the sequence has
 * shown the completion, a fetch that has not caught up yet must not revert it.
 */
export function syncDisplayedCourseCompleted({
  fetchedCompleted,
  fetchedHighest,
  displayedCompleted,
  displayedHighest,
  pendingCompletedLevelIndex,
  totalLevels,
}: DisplayedCourseCompletionInput): boolean {
  if (displayedCompleted && fetchedHighest < displayedHighest) {
    return true;
  }

  const completionNotYetShown =
    pendingCompletedLevelIndex !== null &&
    pendingCompletedLevelIndex > displayedHighest;
  const isFinalLevel =
    totalLevels > 0 && pendingCompletedLevelIndex !== null
      ? pendingCompletedLevelIndex + 1 >= totalLevels
      : false;

  if (completionNotYetShown && isFinalLevel && fetchedCompleted) {
    return false;
  }

  return fetchedCompleted;
}
